import React, { useEffect, useRef } from 'react';
import {
  GridCellParams,
} from '@material-ui/data-grid';

import './DataEntryGrid.scss';
import { KPI_TYPES } from '../../../hooks/useLibrary';
import { DataEntryTooltip } from './DataEntryGrid';

const cellWidth = 142;

export function useDataEntryGrid ({
  columns = [],
  data = [],
  editable = false,
  onRowsChange,
  scrolledOnceGrid,
  onScrolledOnce,
  showLastPeriod = true,
}: {
  columns: any[];
  data: any[];
  editable: boolean;
  onRowsChange: any;
  scrolledOnceGrid: any;
  onScrolledOnce: any;
  showLastPeriod?: boolean;
}) {
  const el = useRef<any>(null);
  const arrowLeftRef = useRef<any>();
  const arrowRightRef = useRef<any>();

  const handleCellEditCommit = React.useCallback(
    ({ id, field, value, props }) => {
      const row = data.find((item) => item.id === id);
      if (!row) {
        return;
      }

      // MUI DataGrid v4 emits committed edit value under params.props.value.
      // Keep support for params.value to remain compatible with newer callbacks.
      value = value ?? props?.value;

      const type = row.type;
      let is_negative;
      if (value !== undefined) {
        value = value.toString();
        if (['Fraction', '%'].includes(type) || type.startsWith('Currency')) {
          // Clean input
          value = value.replace(/[^0-9.-]/g, '');
          is_negative = value.startsWith('-');
          value = value.replace(/[^0-9.]/g, '');
          if (value === '') {
            onRowsChange({ rowId: id, column: field, value });
            return;
          }
          value = parseFloat(value);
          if (is_negative) value *= -1;

          // Allow at least two decimals
          value = value.toString();
          const dot_index = value.indexOf('.') + 1;
          const precision = !dot_index ? 0 : value.length - dot_index;
          if (precision < 2) value = parseFloat(value).toFixed(2).toString();

        } else if (type === 'Number (Whole)') {
          value = value.replace(/[^0-9-]/g, '');
          is_negative = value.startsWith('-');
          value = value.replace(/[^0-9]/g, '');
          if (value === '') {
            onRowsChange({ rowId: id, column: field, value });
            return;
          }
          value = parseInt(value);
          if (is_negative) value *= -1;
          value = value.toString();
        }
      }
      onRowsChange({ rowId: id, column: field, value });
    },
    [onRowsChange, data],
  );

  const scroll = (direction: any) => {
    const container = el.current.querySelector('.MuiDataGrid-window');
    if (direction === 'left') {
      container.scrollLeft = container.scrollLeft + cellWidth;
    } else {
      container.scrollLeft = container.scrollLeft - cellWidth;
    }
  };

  function formatNumber(value: number) {
    return Intl.NumberFormat('en-US').format(value);
  }

  function KpiTypeFormatter({ row, column }: { row: any; column: any }) {
    const value = row[column.field];

    if (!value || value === '') return '';

    const sign = value <= 0 ? '-' : '';
    switch (row.type) {
      case KPI_TYPES.CURRENCY_EUR:
        return `${sign}€${formatNumber(Math.abs(value))}`;
      case KPI_TYPES.CURRENCY_GBP:
        return `${sign}£${formatNumber(Math.abs(value))}`;
      case KPI_TYPES.CURRENCY_USD:
        return `${sign}$${formatNumber(Math.abs(value))}`;
      case KPI_TYPES.FRACTION:
        return `${value}`;
      case KPI_TYPES.NUMBER:
        return `${formatNumber(value)}`;
      case KPI_TYPES.PERCENT:
        return `${value}%`;
      case KPI_TYPES.TEXT:
        return `${value}`;
      default:
        return '❌ unsupported type';
    }
  }

  if (!showLastPeriod) {
    columns = columns.slice(0, -1);
  }
  const formattedColumns: any[] = columns.map<any>(
    (col: any, index: number) => {
      return {
        field: col.field,
        headerName: col.headerName,
        headerClassName: index
          ? index + (showLastPeriod ? 2 : 1) == columns.length
            ? 'header-current'
            : ''
          : 'frozen',
        headerAlign: 'right',
        cellClassName: index
          ? index + (showLastPeriod ? 2 : 1) == columns.length
            ? 'cell-current'
            : ''
          : 'frozen',
        align: 'right',
        renderCell: (params: GridCellParams) => {
          const value = KpiTypeFormatter({ row: params.row, column: params });
          if (String(value).length > 15) {
            return (
              <DataEntryTooltip
                arrow
                title={value}
                placement="bottom"
              >
                <span>{value}</span>
              </DataEntryTooltip>
            );
          } else {
            return value;
          }
        },
        width: cellWidth,
        editable: index && editable,
        sortable: false,
      };
    },
  );

  useEffect(() => {
    if (el) {
      const height = Math.min(350, data.length * 35 + 65);
      if (el.current) {
        el.current.style.height = height + 'px';
      }
    }

    const container = el.current.querySelector('.MuiDataGrid-window');
    function onScroll() {
      if (
        container.scrollLeft + container.clientWidth <
        container.scrollWidth - cellWidth
      ) {
        arrowRightRef.current.style.display = '';
        arrowRightRef.current.style.opacity = 1;
      } else {
        arrowRightRef.current.style.opacity = 0;
        setTimeout(() => {
          if (arrowRightRef.current.style.opacity === '0')
            arrowRightRef.current.style.display = 'none';
        }, 250);
      }

      if (container.scrollLeft < cellWidth) {
        arrowLeftRef.current.style.opacity = 0;
        setTimeout(() => {
          if (arrowLeftRef.current.style.opacity === '0')
            arrowLeftRef.current.style.display = 'none';
        }, 250);
      } else {
        arrowLeftRef.current.style.display = '';
        arrowLeftRef.current.style.opacity = 1;
      }
    }
    if (container) {
      container.addEventListener('scroll', onScroll);
      setTimeout(() => {
        if (el && !scrolledOnceGrid) {
          onScrolledOnce();
          container.scrollLeft =
            container.scrollWidth - container.clientWidth - cellWidth - 1;
        }
        onScroll();
      }, 500);
    }
    return () => container && container.removeEventListener('scroll', onScroll);
  }, [data]);

  return {
    el,
    arrowLeftRef,
    arrowRightRef,
    handleCellEditCommit,
    scroll,
    formattedColumns,
  };
};
